/**
 * Selected shipping/billing contact state for the sales-order form.
 *
 * Holds the two contacts the order form submits (shipping and billing) and
 * seeds them when opening an existing order for edit or duplicating one — a
 * saved order restores its stored contact FK, while a reconstructed or
 * duplicated order is seeded as a synthetic (non-FK) contact from the order's
 * address fields.
 *
 * Contains:
 * - useOrderSaleContacts — selected-contact state + edit/duplicate seeding.
 */
import { useEffect, useState } from 'react';

/**
 * @param {object}   source
 * @param {object?}  source.orderSale     - Existing order being edited, or null.
 * @param {object?}  source.duplicateFrom - Order being duplicated from, or null.
 * @returns {{
 *   selectedShippingContact: object|null,
 *   setSelectedShippingContact: Function,
 *   selectedBillingContact: object|null,
 *   setSelectedBillingContact: Function,
 * }}
 */
export function useOrderSaleContacts({ orderSale, duplicateFrom }) {
  const [selectedShippingContact, setSelectedShippingContact] = useState(null);
  const [selectedBillingContact, setSelectedBillingContact] = useState(null);

  // Load existing order contacts for edit mode (combined contact + address),
  // or reconstruct from duplicateFrom for new duplicate orders.
  useEffect(() => {
    const src = orderSale ?? duplicateFrom;
    if (!src) return;
    if (src.ship_contact || src.ship_email || src.ship_address) {
      const hasSavedContact = orderSale ? orderSale.shipping_contact != null : false;
      setSelectedShippingContact({
        _isSynthetic: !hasSavedContact,
        id: orderSale?.shipping_contact || null,
        name: src.ship_contact || '',
        business_name: src.ship_company_name || '',
        email: src.ship_email || '',
        phone1: src.ship_phone || '',
        address: src.ship_address || '',
        city: src.ship_city || '',
        state: src.ship_state || '',
        zipcode: src.ship_zip || '',
        country: src.ship_country || '',
      });
    }
    if (src.bill_contact || src.bill_email || src.bill_address) {
      const hasSavedContact = orderSale ? orderSale.billing_contact != null : false;
      setSelectedBillingContact({
        _isSynthetic: !hasSavedContact,
        id: orderSale?.billing_contact || null,
        name: src.bill_contact || '',
        business_name: src.bill_company_name || '',
        email: src.bill_email || '',
        phone1: src.bill_phone || '',
        address: src.bill_address || '',
        city: src.bill_city || '',
        state: src.bill_state || '',
        zipcode: src.bill_zip || '',
        country: src.bill_country || '',
      });
    }
  }, [orderSale, duplicateFrom]);

  return {
    selectedShippingContact,
    setSelectedShippingContact,
    selectedBillingContact,
    setSelectedBillingContact,
  };
}
